import {
  Action, ActionIcon, FormHandlers, ICON_INVOICE_OUTLINE, Portal, useModal,
} from '@doveit/bricks';
import React from 'react';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { Invoice } from '../../../providers/api/dtos';
import UpsertInvoice, { UpsertInvoiceProps } from '../upsert-invoice/UpsertInvoice';
import { InvoiceStatus, InvoiceType } from '../../../domain/types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

export type UpsertInvoiceActionProps = {
  type?: InvoiceType,
  disabled?: boolean,
  onSuccess?: (invoice: Invoice) => void,
  children?: ({ disabled, openUpsertInvoiceModal }: { disabled: boolean, openUpsertInvoiceModal?: VoidFunction }) => React.ReactNode,
} & Omit<UpsertInvoiceProps, 'formRef' | 'hiddenFields'>;

const UpsertInvoiceAction: React.FC<UpsertInvoiceActionProps> = ({
  invoice,
  type,
  assignmentId,
  reference,
  disabled = false,
  disabledFields,
  onSuccess,
  children,
}) => {
  const [formActionDisabled, setFormActionDisabled] = React.useState(false);
  const { userIsAdmin, userIsFinance } = useRBAC();

  const upsertInvoiceModal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const computedLabel = React.useMemo(() => (invoice?.id ? 'Modifica' : 'Aggiungi'), [invoice?.id]);

  const hiddenFieldsByStatus = React.useMemo((): UpsertInvoiceProps['hiddenFields'] => {
    if (!invoice) {
      return {
        invoiceId: true, proformaInvoiceId: true, vat: true, paidValue: true, paymentMethod: true, notes: true,
      };
    }

    switch (invoice.status) {
      case InvoiceStatus.DRAFT:
        return {
          invoiceId: true, proformaInvoiceId: true, vat: true, paidValue: true, paymentMethod: true, notes: true,
        };
      case InvoiceStatus.NOT_PAID:
      case InvoiceStatus.PROFORMA:
        return { invoiceId: true, paidValue: true, paymentMethod: true };
      case InvoiceStatus.PAID:
      case InvoiceStatus.COLLECTED:
      default:
        return undefined;
    }
  }, [invoice]);

  const openUpsertInvoiceModal = React.useCallback(() => {
    upsertInvoiceModal.open();
  }, [upsertInvoiceModal]);

  const onUpsertInvoiceSuccess = React.useCallback((savedInvoice: Invoice) => {
    upsertInvoiceModal.close();

    if (onSuccess) {
      onSuccess(savedInvoice);
    }
  }, [upsertInvoiceModal, onSuccess]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  if (invoice?.id && !userIsAdmin && !userIsFinance) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          disabled,
          openUpsertInvoiceModal: disabled
            ? undefined
            : openUpsertInvoiceModal,
        })
        : (
          <ActionIcon
            icon={{ path: ICON_INVOICE_OUTLINE }}
            aria-label="Azione per aggiungere o aggiornare un pagamento"
            label={computedLabel}
            onClick={openUpsertInvoiceModal}
            disabled={disabled}
          />
        )}

      <Portal>
        <SimpleModal
          {...upsertInvoiceModal}
          title={`${computedLabel} pagamento`}
          footer={(
            <Action
              label={`${computedLabel} pagamento`}
              color="primary"
              emphasis="high"
              aria-label="Aggiungi o aggiorna un pagamento"
              disabled={formActionDisabled}
              onClick={submitForm}
            />
          )}
        >
          {invoice
            ? (
              <UpsertInvoice
                formRef={formRef}
                invoice={invoice}
                onSuccess={onUpsertInvoiceSuccess}
                onFormStatusChanged={setFormActionDisabled}
                hiddenFields={hiddenFieldsByStatus}
                disabledFields={disabledFields}
              />
            )
            : (
              <UpsertInvoice
                formRef={formRef}
                assignmentId={assignmentId}
                reference={reference}
                onSuccess={onUpsertInvoiceSuccess}
                onFormStatusChanged={setFormActionDisabled}
                hiddenFields={hiddenFieldsByStatus}
                disabledFields={disabledFields}
                type={type}
              />
            )}
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpsertInvoiceAction;
